import { observable } from "mobx";
import { api, apiOperacao } from "../../api";
import {
  IEmpresa,
  IReceita,
  newEmpresa,
  newIReceita,
} from "../../model";
import { toast } from "react-toastify";
import { decodeUtf8String, downloadBinaryFile, nameFileZip, toast_error } from "../../utils";
import { LookupStore } from "../LookupStore";

export class _DeiStore {
  obs = observable({
    selectEmpresaDae: newEmpresa() as IEmpresa,
    selectedPeriodDae: [] as any,
    loading_arquivos: false as boolean,

    selectedEmpresa: newEmpresa() as IEmpresa,
    modelo: "NF-E" as string,
    tipo: "Entrada" as string,
    summary: [] as any,

    receita: newIReceita() as IReceita,
    date_calculo: null as Date,
    date_vencimento: null as Date,

    selectedTab: "dei" as string,

    loading_tasks: false,
    loadingArquivoAll: false,
    loading_erros: false,
    tasks_dei_avulso: [],
    tasks_dei_length: 0,
  })

  /**
   * Envia uma solicitação de geração de DAE/DEI para a API, com base nos dados informados.
   * 
   * A função monta o payload com o CNPJ da empresa, a data de referência e a lista de receitas com seus respectivos valores.
   * Em seguida, envia a requisição para o endpoint `/dei/transmite`.
   * 
   * Exibe mensagens de sucesso ou erro utilizando o sistema de notificações (`toast`), conforme o resultado da operação.
   * 
   * @param {Object} params Objeto com os dados da solicitação.
   * @param {string} params.cnpj CNPJ da empresa para a qual será feita a solicitação.
   * @param {string} params.data_referencia Data de referência da apuração (formato `MM/YYYY`).
   * @param {{ nome: string, valor: number }[]} params.receitas Lista de receitas com nome e valor a serem transmitidas.
   * 
   * @returns {Promise<void>} Retorna uma `Promise` que se resolve após o envio da solicitação.
  */

  async solicita_dei({cnpj, data_referencia, receitas}: {
      cnpj: string,
      data_referencia: string,
      receitas: { nome: string, valor: number }[]
    }) {

    const payload = {
      cnpj,
      data_referencia,
      receitas
    };

    try {
      const resp = await apiOperacao("dei").post('/dei/transmite', payload);
  
      if (resp.data.ok) {
        
        toast.success('Solicitação DEI realizada com sucesso!', {});

      } else {

        toast.error('Não foi possível realizar a solicitação do DEI, tente novamente mais tarde.', {});

      }
  
    } catch (ex) {
      toast_error(ex, `Erro ao solicitar DEI. Tente novamente mais tarde.`);
    }
  }

  /**
   * Realiza o download de um arquivo `.zip` contendo os documentos DAE/DEI gerados para uma determinada empresa.
   * 
   * A função envia uma requisição para o endpoint `/dei/download`, com o `task_id` da solicitação e os códigos das receitas.
   * Ao receber a resposta com o arquivo binário, o conteúdo é baixado no navegador com um nome gerado dinamicamente.
   * 
   * Exibe mensagens de erro caso a resposta da API contenha falhas ou a requisição falhe.
   * Também atualiza o estado de carregamento (`loading_arquivos`) durante o processo.
   * 
   * @param {IEmpresa} empresa Objeto da empresa para quem os arquivos foram gerados (usado para nomear o arquivo).
   * @param {string} task_id Identificador da tarefa DAE/DEI previamente transmitida.
   * @param {string[]} receitas Lista de códigos de receitas cujos documentos devem ser incluídos no download.
   * 
   * @returns {Promise<void>} Retorna uma `Promise` que se resolve após o término do download ou falha da operação.
  */

  async downloadArquivo(empresa: IEmpresa, task_id: string, receitas: string[]) {

    this.obs.loading_arquivos = true;

    try {

      let params = {
        task_id: task_id,
        receitas: receitas
      }

      let resp = await apiOperacao("dei").post("/dei/download", params, { responseType: 'blob' });

      if (resp.status == 200) {

        let nome = nameFileZip(empresa, "DEI");

        downloadBinaryFile(resp.data, nome)

      } else {

        toast.error(decodeUtf8String(resp.headers.msg), {});

      }

    } catch (ex) {

      toast_error(ex, `Erro ao realizar download DEI, tente novamente mais tarde.`);

    }

    this.obs.loading_arquivos = false

  }

  /**
   * Formata os valores de apuração DAE/DEI recebidos da API em uma estrutura detalhada por tipo de débito (ICMS, FCP e ICMS 1139).
   * 
   * A função percorre a lista de resumos retornados pela API (`resp.data.summary`) e, para cada item, consulta a classificação
   * correspondente no `LookupStore` (via `getIcmsClassificacaoDAE`). Com base nisso, ela gera até três entradas individuais:
   * uma para ICMS, uma para FCP e uma para ICMS 1139, preenchendo os campos adicionais utilizados na interface.
   * 
   * Mesmo que os valores sejam 0, os itens são gerados (as verificações de valor estão comentadas, mas podem ser ativadas conforme necessário).
   * 
   * @param {any} resp Resposta da API contendo a propriedade `data.summary`, que deve ser uma lista de objetos de apuração.
   * 
   * @returns {any[]} Retorna uma lista achatada (`flatMap`) contendo objetos formatados para cada tipo de débito (ICMS, FCP, 1139),
   * prontos para uso na interface e seleção.
  */

  formatter_valores(resp) {

    return resp.data.summary?.flatMap((value) => {

      const classificacao = LookupStore.getIcmsClassificacaoDAE(value.icms_classificacao_id);

      const itens = []

      if (classificacao) {

        if (value.valor_icms > 0) {
          itens.push({
            ...value,
            campo: "ICMS",
            outros_debitos: [{ nome: "ICMS", valor: value.valor_icms }, { nome: "FCP", valor: 0 }, { nome: "ICMS 1139", valor: 0 }],
            name: classificacao?.nome + " - ICMS",
            dae_avulso: classificacao?.dae_avulso ?? false,
            dei: classificacao?.dei ?? false,
            codigo_receita: classificacao?.codigo_receita ?? "",
          })
        }

        if (value.valor_fcp > 0) {
          itens.push({
            ...value,
            campo: "FCP",
            outros_debitos: [{ nome: "ICMS", valor: 0 }, { nome: "FCP", valor: value.valor_fcp }, { nome: "ICMS 1139", valor: 0 }],
            name: classificacao?.nome + " - FCP",
            dae_avulso: classificacao?.dae_avulso ?? false,
            dei: classificacao?.dei ?? false,
            codigo_receita: classificacao?.codigo_receita ?? "",
          })
        }

        if (value.valor_1139 > 0) {
          itens.push({
            ...value,
            campo: "1139",
            outros_debitos: [{ nome: "ICMS", valor: 0 }, { nome: "FCP", valor: 0 }, { nome: "ICMS 1139", valor: value.valor_1139 }],
            name: classificacao?.nome + " - ICMS 1139",
            dae_avulso: classificacao?.dae_avulso ?? false,
            dei: classificacao?.dei ?? false,
            codigo_receita: classificacao?.codigo_receita ?? "",
          })
        }

      }

      return itens

    });

  }

  async handleDaePaged(paged: number, cnpj: string, data_start: string, data_end: string, tipo?: "simples" | "dae_avulso") {
    this.obs.loading_tasks = true;

    if (!cnpj) {
      toast.warn("Selecione uma empresa para a consulta.");
      this.obs.loading_tasks = false;
      return;
    }

    if (!data_start || !data_end || data_end == "Data inválida" || data_start == "Data inválida") {
      toast.warn("Selecione o período da consulta.");
      this.obs.loading_tasks = false;
      return;
    }

    try {
      let data = {
        page: paged,
        limit: 15,
        cnpj: cnpj,
        data1: data_start,
        data2: data_end,
        tipo: tipo
      }

      let resp = await apiOperacao("dei").post("/dei/consulta-lote", data);

      this.obs.tasks_dei_avulso = [];
      this.obs.tasks_dei_length = 0;

      if (resp.data) {

        let itens = resp.data.itens;

        if (tipo) {
          itens = itens.filter(task => task.tipo === tipo);
        }

        this.obs.tasks_dei_length = itens.length;
        this.obs.tasks_dei_avulso = itens;

      } else {
        toast.warn('Nenhum detalhe para exibir.', {});
      }

    } catch (ex) {
      toast_error(ex, `Erro ao consultar Erro Tarefa, tente novamente mais tarde.`);
    }

    this.obs.loading_tasks = false;

  }

}

export const DeiStore = new _DeiStore();
